import { useEffect, useState } from "react";
import { api, UnauthorizedError } from "@/api/client";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { X, Copy, Check, Share2, Lock, Globe } from "lucide-react";

type Turn = { role: "user" | "bot"; text: string };

const EXPIRY: { v: number; label: string }[] = [
  { v: 7, label: "7일" },
  { v: 30, label: "30일" },
  { v: 90, label: "90일" },
  { v: 0, label: "무기한" },
];

export default function ShareDialog({
  open,
  onClose,
  title,
  turns,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  turns: Turn[];
}) {
  const [days, setDays] = useState(30);
  const [requireLogin, setRequireLogin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // 열릴 때마다 초기화
  useEffect(() => {
    if (open) {
      setDays(30);
      setRequireLogin(false);
      setUrl(null);
      setCopied(false);
      setCreating(false);
    }
  }, [open]);

  if (!open) return null;

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.shareCreate(title, turns, { expires_days: days, require_login: requireLogin });
      const full = `${window.location.origin}${r.path}`;
      setUrl(full);
      try {
        await navigator.clipboard.writeText(full);
        setCopied(true);
        toast("공유 링크를 복사했습니다.", "success");
      } catch {
        /* 복사 실패해도 링크는 표시 */
      }
    } catch (e) {
      toast(e instanceof UnauthorizedError ? "로그인이 필요합니다." : "공유 링크 생성에 실패했습니다.", "error");
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("공유 링크 (복사하세요)", url);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-semibold">
            <Share2 className="h-4 w-4 text-[#EB4E26]" /> 대화 공유
          </span>
          <button onClick={onClose} aria-label="닫기" className="rounded-md p-1 text-muted-foreground hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!url ? (
          <div className="space-y-4 text-sm">
            <div>
              <div className="mb-1.5 text-muted-foreground">만료 기한</div>
              <div className="flex gap-1.5">
                {EXPIRY.map((e) => (
                  <button
                    key={e.v}
                    onClick={() => setDays(e.v)}
                    className={cn(
                      "flex-1 rounded-lg border px-2 py-1.5",
                      days === e.v
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card hover:bg-muted",
                    )}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-muted-foreground">접근 범위</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => setRequireLogin(false)}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2",
                    !requireLogin
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-muted",
                  )}
                >
                  <Globe className="h-4 w-4" /> 링크 공개
                </button>
                <button
                  onClick={() => setRequireLogin(true)}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2",
                    requireLogin
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-muted",
                  )}
                >
                  <Lock className="h-4 w-4" /> 사내 로그인
                </button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {requireLogin
                  ? "링크를 받은 사람도 사내 로그인을 해야 열람할 수 있습니다."
                  : "링크를 아는 사람은 로그인 없이 열람할 수 있습니다."}
              </p>
            </div>

            <button
              onClick={create}
              disabled={creating || turns.length === 0}
              className="w-full rounded-lg bg-primary py-2 font-medium text-primary-foreground disabled:opacity-40"
            >
              {creating ? "만드는 중…" : "공유 링크 만들기"}
            </button>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              링크가 생성되었습니다{copied ? " (복사됨)" : ""}. 동료에게 전달하세요.
            </p>
            <div className="flex items-center gap-1.5">
              <input
                readOnly
                value={url}
                className="flex-1 truncate rounded-lg border border-border bg-muted px-2 py-2 text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                onClick={copy}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"
                aria-label="복사"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {requireLogin ? "사내 로그인 필요 · " : "링크 공개 · "}
              {days === 0 ? "무기한" : `${days}일 후 만료`} · 즐겨찾기/공유 목록에서 해제할 수 있습니다.
            </p>
            <button onClick={onClose} className="w-full rounded-lg border border-border py-2">
              닫기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
